import {Dispatch, PropsWithChildren, useMemo, useReducer} from "react";
import {createContext} from "../../../../../context/create-context";
import {GetStoreServicesOrdersApiResponse} from "../../../../../react-query-hooks/store/services/get-orders/types";
import {PaginationMeta} from "../../../../../redux/service/i-service";
import {useGetCustomerStoreServicesOrders} from "../../../../../react-query-hooks/store/services/get-orders";
import {
  getCustomerStoreServicesOrdersFormReducer,
  GetCustomerStoreServicesOrdersReducerActions,
  initialGetCustomerStoreServicesOrdersReducerState,
} from "../../reducers/customer";

type GetCustomerStoreServicesOrdersContextState = {
  state: typeof initialGetCustomerStoreServicesOrdersReducerState;
  dispatch: Dispatch<GetCustomerStoreServicesOrdersReducerActions>;
  orders: GetStoreServicesOrdersApiResponse["orders"];
  paginationMeta: PaginationMeta | undefined;
  isLoading: boolean;
  isError: boolean;
  isPreviousData: boolean;
};

const [useContext, ContextProvider] =
  createContext<GetCustomerStoreServicesOrdersContextState>();

export const useGetCustomerStoreServicesOrdersContext = useContext;

export const GetCustomerStoreServicesOrdersContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [state, dispatch] = useReducer(
    getCustomerStoreServicesOrdersFormReducer,
    initialGetCustomerStoreServicesOrdersReducerState
  );

  const {data, isLoading, isError, isPreviousData} = useGetCustomerStoreServicesOrders({
    statusId: state.statusId,
    page: state.currentPage,
    per_page: state.per_page,
  });

  const value = useMemo(() => {
    const orders = data?.data.orders ?? [];
    const paginationMeta = data?.meta;

    return {
      state,
      dispatch,
      orders,
      paginationMeta,
      isLoading,
      isError,
      isPreviousData,
    };
  }, [
    state,
    dispatch,
    data, // only depends on raw query data
    isLoading,
    isError,
    isPreviousData,
  ]);

  return <ContextProvider value={value}>{children}</ContextProvider>;
};
